import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {Actions, ofType} from '@ngrx/effects';
import {merge, Subject, Subscription} from 'rxjs';
import {filter, map, skip, take, takeUntil} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {environment} from '@environments/environment';
import {
  Campaign,
  InfluencerSso,
  InfluencerType,
  Project,
  ProjectInfluencerSegment,
  User
} from '@app/modules/shared/models';
import {InfluencerSsoService} from '@app/services/influencer-sso.service';
import {
  CampaignGetAll,
  CampaignGetAllSuccess,
  getCampaignError,
  getCampaigns,
  getInfluencerError,
  getInfluencersProjectSegment,
  getInfluencersTypes,
  getLoggedInUser,
  getProjectError,
  getProjectSuccessResponse,
  ICampaignState,
  IInfluencersState,
  InfluencersProjectSegment,
  InfluencersTypeList,
  ProjectUpdate
} from '@app/stores';
import {
  AccessMethod,
  accessQuery,
  enabledSetting,
  expiresInDays,
  INFLUENCER_TYPE_PARAM,
  isEnabled,
  KeyKind,
  linkSnippets,
  NO_SNIPPETS,
  parseWidgetSettings,
  SigningSnippets,
  SignMode,
  TabMethod
} from './share-widget-access.model';
import {
  AccessSecurityState,
  savedAccessSecurityOf,
  ShareWidgetAccessSecurityComponent
} from './share-widget-access-security.component';
import {AccessUrlField, accessUrlFields, PLATFORM_ID_SAMPLE} from './share-widget-access-fields';

/** The key each kind's sample link opens. */
const SAMPLE_KEYS: Record<KeyKind, string> = {email: 'user@email.com', platformId: PLATFORM_ID_SAMPLE};

const NO_DEFAULT_CAMPAIGN = 'No default widget campaign set';

/** Campaigns for a picker: the default widget campaign first and named as such, then the rest. */
function campaignOptionsWithDefault<T extends {id: any; name?: string; code?: string}>(
  campaigns: T[],
  defaultCampaignId: unknown
): T[] {
  const isDefault = (campaign: T) => !!defaultCampaignId && Number(campaign.id) === Number(defaultCampaignId);
  return [
    ...campaigns.filter(isDefault),
    ...campaigns.filter((campaign) => !isDefault(campaign))
  ].map((campaign) => ({
    ...campaign,
    name: `${campaign.name} (${campaign.code})${isDefault(campaign) ? ' - default widget campaign' : ''}`
  }));
}

/** What a campaign picker left empty uses: the default widget campaign, named once it is loaded. */
function defaultCampaignFallback(
  defaultCampaignId: unknown,
  defaultCampaign: {name?: string; code?: string} | null
): string {
  if (!defaultCampaignId) return NO_DEFAULT_CAMPAIGN;
  return defaultCampaign
    ? `Uses widget's default campaign: ${defaultCampaign.name} (${defaultCampaign.code})`
    : 'Uses widget\'s default campaign';
}

/**
 * A campaign's segments in the server's order (CampaignInfluencerSegment list: sort_order, then
 * id), so the first is the one a link without a segment registers into. MySQL sorts a missing
 * sort_order first.
 */
function bySegmentOrder(first: any, second: any): number {
  const rank = (segment: any) => segment.sort_order == null ? -Infinity : Number(segment.sort_order);
  if (rank(first) !== rank(second)) return rank(first) < rank(second) ? -1 : 1;
  return Number(first.id) - Number(second.id);
}

/** The links' base: the panel's front URL, without its hash route or trailing slash. */
function frontBase(): string {
  return (environment.frontUrl || '').replace('/#', '').replace(/\/+$/, '');
}

/** One path segment, encoded; @ stays as it is, as a path allows it and every link so far had it. */
function pathSegment(value: string): string {
  return encodeURIComponent(value).replace(/%40/g, '@');
}

type TargetKey = 'influencer_type' | 'campaign_influencer_segment_code';

interface FieldState {
  value: any;
  activated: boolean;
}

/** A sign mode's part of How to sign: its heading, the line under it and the points under the formula. */
interface HowTo {
  heading: string;
  intro?: string;
  points: string[];
}

/** What a setting covering the project looks like in the listing. */
type ProjectSsoSetting = Pick<InfluencerSso, 'id' | 'name' | 'code' | 'protocol' | 'enabled' | 'callback_url' | 'metadata_url'>;

/** Where loading the project's Influencer SSO settings got to. */
type SsoState = 'loading' | 'ready' | 'none' | 'forbidden' | 'unavailable' | 'failed';

/** What tells the three tabs apart; everything else on them is the same. */
interface AccessTabConfig {
  /** The link's path, under /share/. */
  route: 'pci' | 'pci-join' | 'pci-sso';
  /**
   * The link proves itself with the method's sign option, so the tab has the key selector, the
   * Security section, How to sign and the code. SSO has none of them: the sign-in provider
   * proves who the person is.
   */
  signs: boolean;
  /** What the end of the link may name; an SSO link ends with the SSO setting's code instead. */
  keyKinds: KeyKind[];
  /** Join/Update and SSO: the link may add a new person. */
  addsPeople: boolean;
  /**
   * Join/Update and SSO: the influencer type and segment rows. An Access/Update link reads only
   * the campaign in its path.
   */
  targeting: boolean;
  enableLabel: string;
  enableDescription: string;
  intro: string;
  linkLabel: string;
  /** The link's own description while the sign mode needs a Token / Salt the tab does not have. */
  linkTokenHint: {token: string; salt: string};
  /** What the link's fields do, under Fields. */
  savedFields: string;
  /** Which links may edit, and where every link ends up. */
  editModes: string;
  /** A line under the Fields row it follows, while the key is of that kind. */
  keyNotes: Partial<Record<KeyKind, {after: string; text: string}>>;
  howTo: Partial<Record<SignMode, HowTo>>;
  /** Under the code: how the link behaves opened in a browser, and how to POST it instead. */
  usage: string[];
}

const ADD_THE_TOKEN = 'Add this tab\'s token, exactly as saved, to the link as option_update_token=TOKEN. '
  + 'Every link carries the same token.';
const SALT_NEVER_IN_LINK = 'The key is this tab\'s salt exactly as saved. It never goes in a link.';
const MESSAGE_STARTS_WITH_KEY = 'The message starts with the person\'s key: an email trimmed and lowercased, or a Platform '
  + 'Unique ID trimmed and exactly as written, then "|" and the influencer_type the link names (the same ID can be '
  + 'someone else\'s in another influencer type).';
const LEAVE_EXPIRY_OUT = 'Expires is optional, in unix seconds. For a link that never expires, leave it empty '
  + '(the message then ends with "|") and leave the parameter out.';
const SAVED_FIELDS = 'When the link may edit, the fields it carries are saved to the contact before the page opens, '
  + 'the picture from headshot included. Fields left out of the link are not changed.';
const MAY_EDIT_WITH = 'A link may edit with Request Sign (signed for its key)';
const GLOBAL_TOKEN_EDIT = 'Global Token with "Allow influencer edit via global token" on';
const POST_ANSWER = 'The answer is a 303 to their share page, or {"success": true, "shareUrl": …, "editable": …} '
  + 'with Accept: application/json.';
// When a Join link registers its person: its page's widget does, once the page loads in a browser.
const AS_IT_LOADS = 'once it loads in their browser; a scanner that only fetches the link registers nobody.';
const NOT_SIGNED = {
  linkLabel: 'Generated URL',
  linkTokenHint: {token: '', salt: ''},
  keyNotes: {},
  howTo: {},
  usage: []
};

const TAB_CONFIG: Record<TabMethod, AccessTabConfig> = {
  join: {
    route: 'pci-join',
    signs: true,
    keyKinds: ['email', 'platformId'],
    addsPeople: true,
    targeting: true,
    enableLabel: 'Enable Join/Update Via URL',
    enableDescription: 'Allows /share/pci-join links, and older /share/join links that carry an email or a token. '
      + 'The plain Join page, /share/join/PROJECT_CODE, stays open whatever this says. Saved with Save.',
    intro: 'A Join/Update link adds the person to the campaign, or finds them if they are already a contact, then '
      + 'opens their share page. Send it as a link, or POST it from your server.',
    linkLabel: 'Generated URL',
    linkTokenHint: {
      token: 'Set a token under Security to add it to the Generated URL.',
      salt: 'Set a salt under Security to sign the Generated URL.'
    },
    savedFields: 'A new person is added with the fields the link carries. When the link may edit, they are also saved '
      + 'to an existing contact. Fields left out are not changed. A contact\'s email and Platform Unique ID never change.',
    editModes: `${MAY_EDIT_WITH} or ${GLOBAL_TOKEN_EDIT}. Otherwise an existing contact joins the campaign unchanged. `
      + 'The person lands on their share page, with their update token when they are new or the link may edit them.',
    keyNotes: {
      platformId: {
        after: 'email',
        text: 'To add someone new, the link also needs their email. If that email already belongs to a contact without '
          + 'this ID, the contact joins unchanged and the ID is not saved: give them the ID first, or use their email as the key.'
      },
      email: {
        after: 'platform_unique_id',
        text: 'The email finds the person. A Platform Unique ID in the link is saved only for someone new, and only when no '
          + 'other contact has it. To reach a contact by their ID, use the ID as the key (Request Sign).'
      }
    },
    howTo: {
      none: {
        heading: 'Build this URL',
        intro: 'Sign option None: no token. Anyone with the link can add themselves; the link never edits an existing '
          + 'contact. The key is an email.',
        points: []
      },
      global_token: {
        heading: 'Add the token to this URL',
        intro: `The link carries this tab's token. A valid link opens the Join page, which registers the person ${AS_IT_LOADS} `
          + 'It edits an existing contact only while "Allow influencer edit via global token" is on. A missing or wrong token is '
          + 'refused. The key is an email.',
        points: [ADD_THE_TOKEN]
      },
      request_sign: {
        heading: 'Sign this URL',
        intro: 'Request Sign: the server checks the signature. A link signed for its key opens the Join page, which registers the '
          + `person, with edit access, ${AS_IT_LOADS} A missing, wrong or expired signature is refused. Compute the token on your `
          + 'server for each key and add it to the link.',
        points: [
          SALT_NEVER_IN_LINK,
          MESSAGE_STARTS_WITH_KEY,
          'Between the bars is this project\'s code, as shown. The campaign, the fields and the other parameters are not signed, '
            + 'apart from an ID key\'s influencer_type.',
          `${LEAVE_EXPIRY_OUT} A link past its expiry is refused.`,
          'Add option_update_token=TOKEN, and expires=UNIX_SECONDS when you signed one.'
        ]
      }
    },
    usage: [
      'Opened in a browser, the link shows the Join page, which adds or updates the person there.',
      'Or send it from your server: POST the same URL with the fields, option_update_token and expires in a form or JSON '
        + 'body. The person is added or updated at once, without the Join page and its user agreement box, so send it only '
        + `for people who agreed to your event's terms. ${POST_ANSWER} Multipart bodies are not read. For bulk imports use `
        + 'the public API.'
    ]
  },
  email: {
    route: 'pci',
    signs: true,
    keyKinds: ['email', 'platformId'],
    addsPeople: false,
    targeting: false,
    enableLabel: 'Enable Access/Update via Email in URL',
    enableDescription: 'Allows /share/pci links, which open the contact\'s share page and can update their details. '
      + 'Saved with Save.',
    intro: 'Use this link in emails to your contacts, such as confirmation emails. It never adds anyone.',
    // A sample: each contact's link is this one for their own key.
    linkLabel: 'Sample URL',
    linkTokenHint: {
      token: 'Set a token under Security to add it to the sample link.',
      salt: 'Set a salt under Security to sign the sample link.'
    },
    savedFields: SAVED_FIELDS,
    editModes: `${MAY_EDIT_WITH}, ${GLOBAL_TOKEN_EDIT}, Legacy Sign, or the contact's own update token from their share page `
      + 'link. Any other link (None, or Global Token without edit) leaves the fields unchanged. The link then opens the '
      + 'contact\'s share page, with their update token only when it may edit.',
    keyNotes: {},
    howTo: {
      none: {
        heading: 'How to build the link',
        intro: 'Sign option None: no token. Anyone with the link can open the contact\'s share page; it gives edit access only '
          + 'when it carries the contact\'s own update token.',
        points: []
      },
      legacy_md5: {
        heading: 'How to sign (legacy)',
        points: [
          'Legacy Sign: the token is md5(email + salt), with the email exactly as the contact\'s email is stored and this tab\'s salt '
            + 'exactly as saved. Add it to the link as option_update_token=TOKEN.'
        ]
      },
      global_token: {
        heading: 'How to add the token',
        points: [
          ADD_THE_TOKEN,
          'A link with a missing or wrong token is refused. It gives edit access only while "Allow influencer edit via global token" is on.'
        ]
      },
      request_sign: {
        heading: 'How to sign',
        intro: 'Compute a token for each key and add it to the link:',
        points: [
          SALT_NEVER_IN_LINK,
          MESSAGE_STARTS_WITH_KEY,
          'Between the bars is this project\'s code, as shown. The campaign is not part of the token: the campaign code only goes in '
            + 'the link, and only for a campaign other than the default widget campaign.',
          LEAVE_EXPIRY_OUT,
          'Add option_update_token=TOKEN to the link, and expires=UNIX_SECONDS when you signed one. A missing, wrong or expired '
            + 'signature is refused; a valid one gives the contact edit access.'
        ]
      }
    },
    usage: [
      'Or update the contact from your server: POST the same URL with the fields, option_update_token and expires (when '
        + `you signed one) in a form or JSON body. ${POST_ANSWER}`
    ]
  },
  sso: {
    ...NOT_SIGNED,
    route: 'pci-sso',
    signs: false,
    keyKinds: [],
    addsPeople: true,
    targeting: true,
    enableLabel: 'Enable SSO',
    enableDescription: 'Allows /share/pci-sso links. Saved with Save.',
    intro: 'People join with the sign-in they already have, through your organization\'s identity provider.',
    savedFields: '',
    editModes: ''
  }
};

/** How SSO works, in order, by whether the link shows below yet. */
const SSO_STEPS: Record<'link' | 'noLink', string[]> = (() => {
  const signIn = 'They sign in at the provider, which tells us their email and name.';
  const rest = [
    'Someone new is added to the campaign; someone who is already a contact, of the influencer type the link registers '
      + 'into, is found by their email. Either way they land on their share page and can edit their details.',
    'No token is needed: the provider proves who the person is, so this tab has no sign option.',
    'Fields in the link are used for new people only. A returning person keeps their details, apart from the name and '
      + 'picture their provider sends.'
  ];
  const setUp = 'An identity provider (OIDC or SAML) is set up once as an Influencer SSO setting for this project\'s client.';
  return {
    link: [setUp, `Send people the link below. ${signIn}`, ...rest],
    noLink: [setUp, `Send people this tab's link, which shows here once a setting covers the project. ${signIn}`, ...rest]
  };
})();

const SSO_WARNINGS: Partial<Record<SsoState, string>> = {
  forbidden: 'You do not have access to this project\'s Influencer SSO settings.',
  unavailable: 'Influencer SSO is not available.',
  failed: 'Influencer SSO is not available.'
};

const KEY_DESCRIPTION = 'The end of the link names the person: their email, or with Request Sign your system\'s ID for '
  + 'them (an ID never contains @). An ID link also names their influencer type in influencer_type, signed with the ID.';

type KeyOption = {value: KeyKind; label: string; description?: string; disabled?: boolean};
/** The key selector's options by whether Request Sign is selected: a Platform Unique ID key needs it. */
const KEY_OPTIONS: Record<'signed' | 'unsigned', KeyOption[]> = {
  signed: [{value: 'email', label: 'Email'}, {value: 'platformId', label: 'Platform Unique ID'}],
  unsigned: [
    {value: 'email', label: 'Email'},
    {value: 'platformId', label: 'Platform Unique ID', description: 'A Platform Unique ID key needs Request Sign.', disabled: true}
  ]
};

/** What the Node.js and PHP code does: build the link to send, or POST it from the customer's server. */
type CodeKind = 'link' | 'post';
const CODE_OPTIONS: Array<{value: CodeKind; label: string}> = [
  {value: 'link', label: 'Build the link'},
  {value: 'post', label: 'POST from your server'}
];

/** Everything the link and its code are built from. */
interface LinkInputs {
  /** The link before its token; '' while there is none (no project yet, or no SSO setting). */
  url: string;
  sampleKey: string;
  keyKind: KeyKind;
  /** An ID key's influencer type, which its link names and its signature covers; '' for an email key. */
  keyType: string;
  securityToken: string;
  signMode: SignMode;
  projectCode: string;
  includeExpiry: boolean;
  expires: number | '';
}

const keyOf = (inputs: LinkInputs) => JSON.stringify([
  inputs.url, inputs.sampleKey, inputs.keyKind, inputs.keyType, inputs.securityToken, inputs.signMode,
  inputs.projectCode, inputs.includeExpiry, inputs.expires
]);

/**
 * One Share Widget Auto Access tab, for any method, each with its own settings: Join/Update Via
 * URL, which may add new people, Access/Update via Email in URL, which opens an existing contact's
 * share page, and SSO, which adds or finds people through the organization's identity provider.
 */
@Component({
  selector: 'app-share-widget-access-tab',
  templateUrl: './share-widget-access-tab.component.html',
  styleUrls: ['./share-widget-access-tab.component.scss']
})
export class ShareWidgetAccessTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: number;
  @Input() project: Project;

  /** The tab's method: it shows, signs with and saves this method's settings only. */
  @Input() set method(method: TabMethod) {
    this.accessMethod = method;
    this.fieldOptions = accessUrlFields(method);
    this.fields = {
      ...Object.fromEntries(this.fieldOptions.map((field) => [field.key, {value: field.sample, activated: field.neededForNew}])),
      influencer_type: {value: null, activated: false},
      campaign_influencer_segment_code: {value: null, activated: false}
    };
  }

  get method(): TabMethod {
    return this.accessMethod;
  }

  @ViewChild(ShareWidgetAccessSecurityComponent) securitySection?: ShareWidgetAccessSecurityComponent;

  /** Every field the tab's method lists; visibleFieldOptions are the ones for the key in use. */
  fieldOptions: AccessUrlField[] = [];
  fields: Record<string, FieldState> = {};
  // The campaign targeting rows (Join/Update, SSO), at the end of the table: picked from a
  // drop-down, not a sample.
  readonly targetRows: Array<{key: TargetKey; label: string; description: string}> = [
    {
      key: 'influencer_type',
      label: 'Influencer Type',
      description: 'The registrant\'s influencer type, for example attendee, speaker or partner.'
    },
    {
      key: 'campaign_influencer_segment_code',
      label: 'Campaign Influencer Segment Code',
      description: 'Registers into this segment of the campaign instead of its first one, so it replaces the influencer type.'
    }
  ];
  readonly keyDescription = KEY_DESCRIPTION;
  readonly codeOptions = CODE_OPTIONS;

  // What the end of a signed link names: UI only, never saved.
  keyKind: KeyKind = 'email';
  // Which code the Node.js and PHP fields show: UI only, never saved.
  codeKind: CodeKind = 'link';

  // SSO: the settings covering the project, and the one the link uses.
  ssoState: SsoState = 'loading';
  settings: ProjectSsoSetting[] = [];
  settingOptions: Array<{id: number; name: string}> = [];
  selectedSettingId: number | null = null;
  currentUser: User | null = null;
  /** The Influencer SSO list's filters, for this project. */
  ssoListQuery: {client: number; project: number} | null = null;

  campaigns: Campaign[] = [];
  // Once a campaign list arrived; a failed load is reported by the campaign error toast.
  campaignsLoaded = false;
  /** The campaigns to pick from, the default widget campaign first. */
  campaignOptions: Campaign[] = [];
  defaultCampaignId: number | null = null;
  // The campaign in the link's path; null follows the default widget campaign.
  sampleCampaignId: number | null = null;
  influencerTypes: InfluencerType[] = [];
  segments: any[] = [];

  // What the Security section reports; the saved settings until it reports anything.
  security: AccessSecurityState = {securityToken: '', signMode: 'request_sign', includeExpiry: true, expires: expiresInDays(30)};
  // The Enabled switch at the top; saved by the tab's Save.
  methodEnabled = false;
  saving = false;

  private accessMethod: TabMethod = 'join';
  private projectSegmentsRaw: ProjectInfluencerSegment[] = [];
  // The last link signed and what it was signed for; it only shows while the inputs are still those.
  private signature: {key: string; url: string; failed: boolean} = {key: null, url: '', failed: false};
  // The signing under way, for the inputs as they last changed.
  private signing: {key: string; done: Promise<void>} = {key: null, done: Promise.resolve()};
  private snippetsBuilt: {key: string; snippets: SigningSnippets} = {key: null, snippets: NO_SNIPPETS};
  private segmentOptions: {campaignId: number | null; segments: any[]; options: any[]} = {campaignId: null, segments: null, options: []};
  private typeOptions: {segments: any[]; types: InfluencerType[]; options: InfluencerType[]} = {segments: null, types: null, options: []};
  private saveWatch: Subscription | null = null;
  private targetingRequested = false;
  private destroy$ = new Subject<void>();

  constructor(
    private influencerSsoService: InfluencerSsoService,
    private campaignStore: Store<ICampaignState>,
    private influencerStore: Store<IInfluencersState>,
    private actions$: Actions,
    private toastr: ToastrService,
    // The app store, for the project save and the signed-in user.
    private store?: Store
  ) {
    // The fields of the default method, until the method input sets its own.
    this.method = this.accessMethod;
  }

  ngOnInit(): void {
    this.actions$.pipe(ofType(CampaignGetAllSuccess), takeUntil(this.destroy$))
      .subscribe(() => this.campaignsLoaded = true);
    this.campaignStore.pipe(select(getCampaigns), takeUntil(this.destroy$))
      .subscribe((campaigns) => {
        this.campaigns = campaigns || [];
        this.rebuildCampaignOptions();
        this.rebuildCampaignSegments();
        this.refresh();
      });
    this.campaignStore.pipe(select(getCampaignError), takeUntil(this.destroy$))
      .subscribe((error) => {
        if (error) this.toastr.error(error, 'Error');
      });
    this.campaignStore.dispatch(CampaignGetAll({filter: {project_id: this.projectId}}));
    if (this.config.targeting) this.loadTargetingData();
    if (this.method === 'sso') this.loadSsoData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!(changes.project || changes.method) || !this.project) return;
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get config(): AccessTabConfig {
    return TAB_CONFIG[this.method];
  }

  /** While the method is off, every control on the tab but Enabled and Save is disabled. */
  get locked(): boolean {
    return !this.methodEnabled;
  }

  /** The Enable switch's description, which says what happens while it is off. */
  get enableDescription(): string {
    const base = this.config.enableDescription;
    return this.methodEnabled ? base : `${base} Links of this kind are refused while this is off.`;
  }

  /** Everything from the campaign on shows once there can be a link: an SSO one needs a setting. */
  get linkShown(): boolean {
    return this.method !== 'sso' || this.ssoState === 'ready';
  }

  get selectedSetting(): ProjectSsoSetting | null {
    return this.settings.find((setting) => setting.id === this.selectedSettingId) || null;
  }

  /** The fields for the key in use: an email key is the email itself, an ID key is the ID. */
  get visibleFieldOptions(): AccessUrlField[] {
    return this.fieldOptions.filter((field) => !field.keyKinds || field.keyKinds.includes(this.keyKind));
  }

  /** The key selector's options: a Platform Unique ID key needs Request Sign. */
  get keyOptions(): KeyOption[] {
    return KEY_OPTIONS[this.signMode === 'request_sign' ? 'signed' : 'unsigned'];
  }

  /**
   * The key the sample link names. On Join/Update the email row's sample is the person's email,
   * which an email key carries in the path instead of the query.
   */
  get sampleKey(): string {
    if (this.keyKind === 'email' && this.method === 'join') return this.fields.email?.value || SAMPLE_KEYS.email;
    return SAMPLE_KEYS[this.keyKind];
  }

  get signMode(): SignMode {
    return this.security.signMode;
  }

  get hasSecurityToken(): boolean {
    return !!this.security.securityToken;
  }

  /** Whether the sign mode needs a Token / Salt the section does not have yet. */
  get tokenMissing(): boolean {
    return this.config.signs && this.signMode !== 'none' && !this.hasSecurityToken;
  }

  get howTo(): HowTo {
    return this.config.howTo[this.signMode] || this.config.howTo.request_sign;
  }

  /** Whether each link needs its own token, computed for its key: Request Sign and Legacy Sign. */
  get tokenPerEmail(): boolean {
    return this.signMode === 'request_sign' || this.signMode === 'legacy_md5';
  }

  /** What the key in use is, in running text: the person's email, or their Platform Unique ID. */
  get keyName(): string {
    return this.keyKind === 'platformId' ? 'Platform Unique ID' : 'email';
  }

  /** The key in use, short: "that email", "that ID". */
  get keyWord(): string {
    return this.keyKind === 'platformId' ? 'ID' : 'email';
  }

  /** A code field as tall as its code, so none of it hides behind a scrollbar. */
  codeRows(code: string): number {
    return Math.max(3, (code || '').split('\n').length);
  }

  /** The Request Sign message, for the key in use: an ID with the influencer type its link names. */
  get signingFormula(): string {
    const key = this.keyKind === 'platformId'
      ? `trim(platform_unique_id) + "|" + ${INFLUENCER_TYPE_PARAM}`
      : 'lowercase(trim(email))';
    return `token = hex(HMAC_SHA256(salt, ${key} + "|${this.project?.code ?? ''}|" + expires))`;
  }

  /**
   * The influencer type an ID key's link names, which its signature covers: the type picked under
   * Fields, else the type of the segment picked there, else that of the campaign's first segment
   * (the one the server registers into or looks in). '' for an email key, or while it is not known.
   */
  get keyType(): string {
    if (this.keyKind !== 'platformId') return '';
    const type = this.fields.influencer_type;
    const segmentField = this.fields.campaign_influencer_segment_code;
    if (this.config.targeting && type?.activated && type.value && !segmentField?.activated) return String(type.value);
    const segment = this.config.targeting && segmentField?.activated && segmentField.value
      ? this.segments.find((row) => Number(row.id) === Number(segmentField.value))
      : this.firstSegmentOf(this.sampleCampaign);
    const typeId = segment ? this.projectSegmentOf(segment)?.influencer_type_id : null;
    return typeId != null ? String(typeId) : '';
  }

  /** The link field's own description: what it still needs, if anything. */
  get linkDescription(): string {
    if (!this.tokenMissing) return '';
    const hint = this.config.linkTokenHint;
    return this.signMode === 'global_token' ? hint.token : hint.salt;
  }

  /** What the sign option adds to the Generated URL, under Fields; nothing in None. */
  get signOptionAdds(): string {
    if (!this.config.signs || this.signMode === 'none') return '';
    return this.signMode === 'request_sign' && this.security.includeExpiry ? 'option_update_token and expires' : 'option_update_token';
  }

  // The link. generatedUrl is the link before its token; linkUrl, what the link field shows, adds
  // the token or the signature once it is made for the inputs as they are now. The code follows
  // the same inputs, so the two can never disagree.

  /**
   * The link with the ticked fields and targeting:
   * FRONT/share/ROUTE/PROJECT[/CAMPAIGN]/KEY, an SSO link ending with its setting's code.
   */
  get generatedUrl(): string {
    const last = this.method === 'sso' ? this.selectedSetting?.code : this.sampleKey;
    if (!this.project?.code || !last) return '';
    const query = new URLSearchParams(this.linkFields());
    const suffix = query.toString();
    return `${this.linkPrefix()}${pathSegment(last)}${suffix ? `?${suffix}` : ''}`;
  }

  /** The link signed for the inputs as they are, or '' while it is not (yet). */
  get signedUrl(): string {
    return this.signature.key === this.linkKey ? this.signature.url : '';
  }

  get signingFailed(): boolean {
    return this.signature.failed && this.signature.key === this.linkKey;
  }

  /** What the link field shows: the link with what the sign option adds, the token itself or the signature. */
  get linkUrl(): string {
    return this.signedUrl || this.generatedUrl;
  }

  /** The Node.js and PHP code that builds the link, and POSTs it, the way the sign mode needs it. */
  get snippets(): SigningSnippets {
    const inputs = this.linkInputs();
    const key = keyOf(inputs);
    if (this.snippetsBuilt.key !== key) this.snippetsBuilt = {key, snippets: this.buildSnippets(inputs)};
    return this.snippetsBuilt.snippets;
  }

  /** What the Node.js and PHP fields show: the code that builds the link, or the code that POSTs it. */
  get shownCode(): {node: string; php: string} {
    const {node, php, nodePost, phpPost} = this.snippets;
    return this.codeKind === 'post' ? {node: nodePost, php: phpPost} : {node, php};
  }

  /**
   * Resolves once the link is signed for the inputs as they last changed. The specs wait on it
   * rather than on refresh(), which would rebuild a link a change forgot to.
   */
  get linkSigned(): Promise<void> {
    return this.signing.done;
  }

  get defaultCampaign(): Campaign | null {
    if (!this.defaultCampaignId) return null;
    return this.campaigns.find((campaign) => Number(campaign.id) === Number(this.defaultCampaignId)) || null;
  }

  /** What a campaign picker left empty uses: the default widget campaign. */
  get campaignPlaceholder(): string {
    return defaultCampaignFallback(this.defaultCampaignId, this.defaultCampaign);
  }

  /**
   * Whether to say the default widget campaign is not set or gone: always when none is set,
   * otherwise only once the campaigns loaded without it. A failed load says so on its own.
   */
  get defaultCampaignMissing(): boolean {
    if (this.defaultCampaign) return false;
    return !this.defaultCampaignId || this.campaignsLoaded;
  }

  /** The campaign the link opens: the one chosen, else the default. */
  get sampleCampaign(): Campaign | null {
    return this.chosenCampaign || this.defaultCampaign;
  }

  /** A link to the default campaign carries no campaign code; any other names its campaign. */
  get sampleCampaignInPath(): boolean {
    const campaign = this.sampleCampaign;
    return !!campaign && Number(campaign.id) !== Number(this.defaultCampaign?.id);
  }

  /** The segments of the campaign the link opens, which the segment drop-down offers. */
  get campaignSegments(): any[] {
    const campaignId = this.sampleCampaign ? Number(this.sampleCampaign.id) : null;
    // Kept while its inputs are, so the drop-down keeps its list between change detection runs.
    if (this.segmentOptions.campaignId !== campaignId || this.segmentOptions.segments !== this.segments) {
      this.segmentOptions = {
        campaignId,
        segments: this.segments,
        options: this.segments.filter((segment) => campaignId !== null && Number(segment.campaign_id) === campaignId)
      };
    }
    return this.segmentOptions.options;
  }

  // What each targeting drop-down falls back to while it has no value, as the server decides it.

  get influencerTypePlaceholder(): string {
    return this.firstSegmentFallback('influencer type', (segment) => this.influencerTypeNameOf(segment));
  }

  get segmentPlaceholder(): string {
    return this.firstSegmentFallback('segment', (segment) => segment.name);
  }

  /**
   * The influencer types the link's campaign has a segment for: a link naming any other type is
   * refused. While the campaign's segments are not known, every type, as nothing tells them apart.
   */
  get campaignInfluencerTypes(): InfluencerType[] {
    const segments = this.campaignSegments;
    // Kept while its inputs are, as the segments are, for the drop-down's sake.
    if (this.typeOptions.segments !== segments || this.typeOptions.types !== this.influencerTypes) {
      const typeIds = new Set(segments.map((segment) => this.projectSegmentOf(segment)?.influencer_type_id)
        .filter((id) => id != null).map(Number));
      this.typeOptions = {
        segments,
        types: this.influencerTypes,
        options: typeIds.size ? this.influencerTypes.filter((type) => typeIds.has(Number(type.id))) : this.influencerTypes
      };
    }
    return this.typeOptions.options;
  }

  targetOptions(key: TargetKey): any[] {
    return key === 'influencer_type' ? this.campaignInfluencerTypes : this.campaignSegments;
  }

  targetPlaceholder(key: TargetKey): string {
    return key === 'influencer_type' ? this.influencerTypePlaceholder : this.segmentPlaceholder;
  }

  /** A targeting row's description; with an ID key the link always names the influencer type. */
  targetDescription(row: {key: TargetKey; description: string}): string {
    return row.key === INFLUENCER_TYPE_PARAM && this.keyKind === 'platformId'
      ? `${row.description} An ID link always names one: this, else the segment's or the campaign's first segment's. `
        + 'The ID is signed with it.'
      : row.description;
  }

  /** A ticked segment names the segment, so the influencer type is off while it is. */
  targetBlocked(key: TargetKey): boolean {
    return key === 'influencer_type' && this.fields.campaign_influencer_segment_code.activated;
  }

  /** The line under a Fields row for the key in use, if that row has one. */
  keyNoteAfter(fieldKey: string): string | null {
    const note = this.config.keyNotes[this.keyKind];
    return note && note.after === fieldKey ? note.text : null;
  }

  // SSO: what the settings block says, and the way to the Influencer SSO page.

  get ssoSteps(): string[] {
    return SSO_STEPS[this.linkShown ? 'link' : 'noLink'];
  }

  get ssoWarning(): string | null {
    return SSO_WARNINGS[this.ssoState] || null;
  }

  get ssoNone(): string {
    return this.canAddSso
      ? 'No Influencer SSO setting covers this project yet. Add one for this project\'s client, then come back here for the link.'
      : 'No Influencer SSO setting covers this project yet. Ask your account manager or organization manager to add one.';
  }

  /** Those the Influencer SSO page lets add and edit settings: admins and organization managers. */
  get canAddSso(): boolean {
    return !!this.currentUser?.can('add-influencer-sso', 'influencer-sso-ui');
  }

  /** Those who may open the Influencer SSO page: its menu's users, and those who may add settings there. */
  get canOpenSso(): boolean {
    return this.canAddSso || !!this.currentUser?.can('view-influencer-sso-menu', 'navigation');
  }

  /**
   * The button to the Influencer SSO page, named for what one can do there; '' for none. Without a
   * setting there is only something to do for those who may add one.
   */
  get ssoButtonLabel(): string {
    if (this.ssoState === 'ready') {
      if (this.canAddSso) return 'Manage Influencer SSO';
      return this.canOpenSso ? 'Open Influencer SSO' : '';
    }
    return this.ssoState === 'none' && this.canAddSso ? 'Open Influencer SSO' : '';
  }

  protocolLabel(setting: ProjectSsoSetting): string {
    return String(setting.protocol || '').toUpperCase();
  }

  // Every input the link depends on goes through one of these, and each rebuilds the link.

  onSecurityChange(state: AccessSecurityState): void {
    this.security = state;
    this.keepKeyKindAllowed();
    this.refresh();
  }

  onKeyKindChange(kind: KeyKind): void {
    this.keyKind = kind === 'platformId' ? 'platformId' : 'email';
    this.keepKeyKindAllowed();
    // An ID key's link names its influencer type, which Access/Update (no targeting rows, so nothing
    // loaded yet) reads off the campaign's segments.
    if (this.keyKind === 'platformId' && !this.config.targeting) this.loadTargetingData();
    this.refresh();
  }

  /** Only what the Node.js and PHP fields show; the link stays as it is. */
  onCodeKindChange(kind: CodeKind): void {
    this.codeKind = kind === 'post' ? 'post' : 'link';
  }

  onFieldToggle(key: string, activated: boolean): void {
    this.fields[key].activated = !!activated;
    this.refresh();
  }

  onTargetToggle(key: TargetKey, activated: boolean): void {
    this.fields[key].activated = !!activated;
    if (activated) this.applyTargetExclusivity(key);
    this.refresh();
  }

  setFieldValue(key: string, event: any): void {
    this.fields[key].value = event ? event.value : null;
    if (event) this.applyTargetExclusivity(key);
    this.refresh();
  }

  onSettingSelected(settingId: number | null): void {
    this.selectedSettingId = settingId ? Number(settingId) : null;
    this.refresh();
  }

  onSampleCampaignChange(option: {value: any} | null): void {
    this.sampleCampaignId = option?.value ? Number(option.value) : null;
    // A segment names its campaign, so one of another campaign no longer fits the link; nor does a
    // type the campaign has no segment for.
    for (const [key, options] of [
      ['campaign_influencer_segment_code', this.campaignSegments],
      ['influencer_type', this.campaignInfluencerTypes]
    ] as Array<[TargetKey, any[]]>) {
      const target = this.fields[key];
      if (target.value && !options.some((row) => Number(row.id) === Number(target.value))) target.value = null;
    }
    this.refresh();
  }

  /**
   * Signs the link for the inputs as they are now: Global Token adds the Token / Salt itself;
   * Request Sign the signature (with its expiry), which covers the key and the project code,
   * never the campaign, segment or other parameters; Legacy Sign md5(email + salt). None, and
   * SSO, add nothing. Resolves once signedUrl is current.
   */
  refresh(): Promise<void> {
    const inputs = this.linkInputs();
    const key = keyOf(inputs);
    if (this.signing.key === key) return this.signing.done;
    const {url, sampleKey, keyKind, keyType, securityToken, signMode, projectCode, expires} = inputs;
    const done = !this.config.signs || signMode === 'none' || !url || !securityToken || !projectCode
      ? Promise.resolve()
      : accessQuery(signMode, securityToken, sampleKey, projectCode, expires, keyKind, keyType).then((query) => {
        if (this.signing.key !== key) return;
        this.signature = {key, url: query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : '', failed: false};
      }, () => {
        if (this.signing.key === key) this.signature = {key, url: '', failed: true};
      });
    this.signing = {key, done};
    return done;
  }

  /**
   * Saves this tab's own keys: its switch and, for a signed method, its Security section's three.
   * Everything else, the other tabs' keys and whether Legacy Sign is available included, goes
   * back exactly as loaded: a server that stores widget_settings whole (the one before this
   * model, during a rolling deploy) then loses nothing, and the current one keeps what it has
   * anyway.
   */
  save(): void {
    const loaded = parseWidgetSettings(this.project?.widget_settings);
    if (!loaded) {
      this.toastr.error('The existing widget settings could not be read safely', 'Error');
      return;
    }
    const security = this.config.signs ? this.securitySection : null;
    const problem = security?.tokenProblem;
    if (problem) {
      this.toastr.error(problem, 'Error');
      return;
    }
    const widgetSettings = {
      ...loaded,
      [enabledSetting(this.method)]: !!this.methodEnabled,
      ...(security ? security.pendingSettings() : {})
    };
    this.saving = true;
    this.watchSave();
    this.store.dispatch(ProjectUpdate({project: {...this.project, widget_settings: widgetSettings}, projectId: this.projectId}));
  }

  private get linkKey(): string {
    return keyOf(this.linkInputs());
  }

  private get chosenCampaign(): Campaign | null {
    if (!this.sampleCampaignId) return null;
    return this.campaigns.find((campaign) => Number(campaign.id) === Number(this.sampleCampaignId)) || null;
  }

  /** The tab as the project's settings have it. */
  private initialize(): void {
    const settings = parseWidgetSettings(this.project.widget_settings) || {};
    this.defaultCampaignId = settings.default_new_register_campaign_id || null;
    this.methodEnabled = isEnabled(settings, this.method);
    this.ssoListQuery = {client: this.project.client_id, project: this.projectId};
    if (this.method !== 'sso') {
      this.security = {...this.security, ...savedAccessSecurityOf(this.project, this.method)};
      this.keepKeyKindAllowed();
    }
    this.rebuildCampaignOptions();
    this.refresh();
  }

  /** A Platform Unique ID key needs Request Sign; under any other sign option the key is an email. */
  private keepKeyKindAllowed(): void {
    if (this.signMode !== 'request_sign') this.keyKind = 'email';
  }

  /** The save's answer: the project as saved, or the error the project store reports. */
  private watchSave(): void {
    this.saveWatch?.unsubscribe();
    this.saveWatch = merge(
      this.store.pipe(
        select(getProjectSuccessResponse),
        skip(1),
        filter((project: any) => !!project && Number(project.id) === Number(this.projectId)),
        map((project: Project) => project)
      ),
      this.store.pipe(select(getProjectError), skip(1), filter(Boolean), map((): Project => null))
    ).pipe(take(1), takeUntil(this.destroy$)).subscribe((project) => {
      this.saving = false;
      if (!project) return;
      this.project = project;
      this.initialize();
      this.toastr.success('Settings saved successfully', 'Success');
    });
  }

  private linkInputs(): LinkInputs {
    const {securityToken, signMode} = this.security;
    const includeExpiry = signMode === 'request_sign' && this.security.includeExpiry;
    return {
      url: this.generatedUrl,
      sampleKey: this.sampleKey,
      keyKind: this.keyKind,
      keyType: this.keyType,
      securityToken,
      signMode,
      projectCode: this.project?.code || '',
      includeExpiry,
      expires: includeExpiry ? this.security.expires : ''
    };
  }

  private buildSnippets(inputs: LinkInputs): SigningSnippets {
    if (!this.config.signs || !inputs.url) return NO_SNIPPETS;
    return linkSnippets({
      method: this.method as AccessMethod,
      prefix: this.linkPrefix(),
      keyKind: inputs.keyKind,
      sampleKey: inputs.sampleKey,
      fields: this.linkFields(),
      signMode: inputs.signMode,
      includeExpiry: inputs.includeExpiry,
      projectCode: inputs.projectCode
    });
  }

  /** The link up to its key: the method's route, the project and, for any but the default campaign, the campaign. */
  private linkPrefix(): string {
    const campaignPath = this.sampleCampaignInPath ? `${pathSegment(this.sampleCampaign.code)}/` : '';
    return `${frontBase()}/share/${this.config.route}/${pathSegment(this.project?.code || '')}/${campaignPath}`;
  }

  /**
   * The ticked fields with their samples, then the targeting: what the link carries besides its
   * key. An ID key's link always names its influencer type, which its signature covers.
   */
  private linkFields(): Array<[string, string]> {
    const fields = [...this.tickedFields(), ...this.targetingFields()];
    const keyType = this.keyType;
    return keyType && !fields.some(([name]) => name === INFLUENCER_TYPE_PARAM)
      ? [...fields, [INFLUENCER_TYPE_PARAM, keyType]]
      : fields;
  }

  private tickedFields(): Array<[string, string]> {
    return this.visibleFieldOptions
      .filter((field) => this.fields[field.key].activated && this.fields[field.key].value)
      .map((field): [string, string] => [field.key, String(this.fields[field.key].value)]);
  }

  private targetingFields(): Array<[string, string]> {
    if (!this.config.targeting) return [];
    const segment = this.fields.campaign_influencer_segment_code;
    if (segment.activated && segment.value) {
      const segmentCode = this.getSegmentCodeBySegmentId(segment.value);
      return segmentCode ? [['campaign_influencer_segment_code', segmentCode]] : [];
    }
    const type = this.fields.influencer_type;
    return type.activated && type.value ? [[INFLUENCER_TYPE_PARAM, String(type.value)]] : [];
  }

  private applyTargetExclusivity(key: string): void {
    if (key === 'campaign_influencer_segment_code') this.fields.influencer_type.activated = false;
    if (key === 'influencer_type') this.fields.campaign_influencer_segment_code.activated = false;
  }

  private rebuildCampaignOptions(): void {
    this.campaignOptions = campaignOptionsWithDefault(this.campaigns, this.defaultCampaignId);
  }

  /** Join/Update and SSO: what their targeting rows pick from; Access/Update once an ID key needs a type. Once. */
  private loadTargetingData(): void {
    if (this.targetingRequested) return;
    this.targetingRequested = true;
    this.influencerStore.dispatch(InfluencersTypeList({params: {}}));
    this.influencerStore.dispatch(InfluencersProjectSegment({
      projectId: this.projectId,
      options: JSON.stringify({include_campaign_influencer_segment: true})
    }));
    this.influencerStore.pipe(select(getInfluencersTypes), takeUntil(this.destroy$))
      .subscribe((types) => this.influencerTypes = types || []);
    this.influencerStore.pipe(select(getInfluencersProjectSegment), takeUntil(this.destroy$))
      .subscribe((segments) => {
        this.projectSegmentsRaw = segments || [];
        this.rebuildCampaignSegments();
        this.refresh();
      });
    this.influencerStore.pipe(select(getInfluencerError), takeUntil(this.destroy$))
      .subscribe((error) => {
        if (error) this.toastr.error(error, 'Error');
      });
  }

  /** SSO: who is looking, for the button to the Influencer SSO page, and the settings covering the project. */
  private loadSsoData(): void {
    this.store.pipe(select(getLoggedInUser), takeUntil(this.destroy$))
      .subscribe((user) => this.currentUser = user || null);
    this.ssoState = 'loading';
    this.influencerSsoService.getAllowedForProject(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        if (!response?.success) {
          this.ssoFailed(response);
          return;
        }
        this.settings = response.data || [];
        this.settingOptions = this.settings.map((setting) => ({
          id: setting.id,
          name: setting.enabled ? setting.name : `${setting.name} (disabled)`
        }));
        if (this.settings.length && !this.selectedSetting) {
          this.selectedSettingId = (this.settings.find((setting) => setting.enabled) || this.settings[0]).id;
        }
        this.ssoState = this.settings.length ? 'ready' : 'none';
        this.refresh();
      },
      error: (error) => this.ssoFailed(error)
    });
  }

  /** No access to the listing (403), no Influencer SSO module (404), or anything else, said with a toast too. */
  private ssoFailed(error: any): void {
    this.settings = [];
    if (error?.status === 403) {
      this.ssoState = 'forbidden';
      return;
    }
    if (error?.status === 404) {
      this.ssoState = 'unavailable';
      return;
    }
    this.ssoState = 'failed';
    this.toastr.error(this.errorMessage(error), 'Error');
  }

  /**
   * An empty influencer type or segment takes the first segment, in the server's order, of the
   * campaign the link registers into: the campaign in its path, else the default widget campaign.
   */
  private firstSegmentFallback(what: string, describe: (segment: any) => string | null): string {
    const chosen = this.chosenCampaign;
    const campaign = chosen || this.defaultCampaign;
    const prefix = chosen ? `Uses the selected campaign's ${what}` : `Uses widget's default campaign ${what}`;
    if (!campaign) return this.defaultCampaignId ? prefix : NO_DEFAULT_CAMPAIGN;
    const segment = this.firstSegmentOf(campaign);
    const detail = segment ? describe(segment) : null;
    return detail ? `${prefix}: ${detail}` : prefix;
  }

  /** A campaign's first segment in the server's order: the one a link without a segment or type uses. */
  private firstSegmentOf(campaign: Campaign | null): any | null {
    if (!campaign) return null;
    return this.segments
      .filter((row) => Number(row.campaign_id) === Number(campaign.id))
      .sort(bySegmentOrder)[0] || null;
  }

  /** The project segment a campaign segment belongs to, which says its influencer type. */
  private projectSegmentOf(segment: any): ProjectInfluencerSegment | null {
    return this.projectSegmentsRaw.find((row) => Number(row.id) === Number(segment.project_influencer_segment_id)) || null;
  }

  private influencerTypeNameOf(segment: any): string | null {
    const projectSegment = this.projectSegmentOf(segment);
    if (!projectSegment) return null;
    return projectSegment.influencer_type?.name
      || this.influencerTypes.find((type) => Number(type.id) === Number(projectSegment.influencer_type_id))?.name
      || null;
  }

  private rebuildCampaignSegments(): void {
    if (!this.projectSegmentsRaw.length || !this.campaigns.length) {
      this.segments = [];
      return;
    }
    this.segments = this.projectSegmentsRaw
      .flatMap((item) => item.campaign_segments || [])
      .map((item) => {
        const campaign = this.campaigns.find((row) => row.id === item.campaign_id);
        const segment = this.projectSegmentsRaw.find((row) =>
          row.id === item.project_influencer_segment_id
        );
        if (!campaign || !segment) return null;
        return {
          ...item,
          name: `${campaign.name} - ${segment.name} (${item.code})`
        };
      })
      .filter(Boolean);
  }

  private getSegmentCodeBySegmentId(segmentId: number): string | undefined {
    return this.segments.find((segment) =>
      Number(segment.id) === Number(segmentId)
    )?.code;
  }

  private errorMessage(error: any): string {
    return error?.error?.message || error?.error?.error || error?.message
      || 'Unable to load Influencer SSO configuration';
  }
}
